import React from 'react';

import { ClinicalFlag, EnrollmentHistoryEntry } from '../../types';
import { summarizeEnrollment } from '../../utils/enrollment';
import { EMPTY_VALUE, formatDate, formatEnumLabel } from '../../utils/formatters';
import * as CardStyle from '../../style.module.scss';
import EhrCard from '../EhrCard';
import StatusPill from '../StatusPill';
import { enrollmentStatusTone } from '../StatusPill/tones';
import * as Style from './style.module.scss';

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <>
        <dt className={CardStyle.fieldLabel}>{label}</dt>
        <dd className={Style.value}>{children}</dd>
    </>
);

type Props = {
    entries: EnrollmentHistoryEntry[];
    flags: ClinicalFlag[];
    isLoading: boolean;
};

const Enrollment: React.FC<Props> = ({ entries, flags, isLoading }) => {
    const { timeline, current, enrolledPrograms } = React.useMemo(
        () => summarizeEnrollment(entries),
        [entries]
    );

    return (
        <EhrCard
            title="Enrollment"
            meta="Hero ⇄ DrChrono"
            isLoading={isLoading}
            isEmpty={!timeline.length && !flags.length}
            emptyMessage="No enrollment history or flags on file."
        >
            <dl className={Style.summary}>
                <Row label="Status">
                    {current ? (
                        <StatusPill
                            label={formatEnumLabel(current.status)}
                            tone={enrollmentStatusTone(current.status)}
                        />
                    ) : (
                        EMPTY_VALUE
                    )}
                </Row>
                <Row label="Since">{formatDate(current?.effective_date)}</Row>
                <Row label="Programs">
                    {enrolledPrograms.length ? (
                        <span className={Style.tags}>
                            {enrolledPrograms.map((program) => (
                                <StatusPill key={program} label={program} tone="success" />
                            ))}
                        </span>
                    ) : (
                        EMPTY_VALUE
                    )}
                </Row>
                <Row label="Flags">
                    {flags.length ? (
                        <ul className={Style.flags}>
                            {flags.map((flag) => (
                                <li
                                    key={flag.id}
                                    className={flag.archived ? CardStyle.muted : undefined}
                                >
                                    {flag.flag_text || 'Untitled flag'}
                                    <span className={CardStyle.subText}>
                                        {[
                                            flag.flag_type_name,
                                            flag.archived ? 'Archived' : null,
                                            flag.removed_at
                                                ? `Removed ${formatDate(flag.removed_at)}`
                                                : null
                                        ]
                                            .filter(Boolean)
                                            .join(' · ')}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        EMPTY_VALUE
                    )}
                </Row>
            </dl>

            {timeline.length ? (
                <div className={Style.history}>
                    <span className={CardStyle.fieldLabel}>History</span>
                    <div className={CardStyle.list}>
                        {timeline.map((entry) => (
                            <div key={entry.id} className={CardStyle.listRow}>
                                <div>
                                    <span className={CardStyle.primary}>
                                        {entry.program || EMPTY_VALUE}
                                    </span>
                                    <span className={CardStyle.muted}>
                                        {` · ${formatDate(entry.effective_date)}`}
                                    </span>
                                    {entry.reason ? (
                                        <span className={CardStyle.subText}>{entry.reason}</span>
                                    ) : null}
                                </div>
                                <StatusPill
                                    label={formatEnumLabel(entry.status)}
                                    tone={enrollmentStatusTone(entry.status)}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            ) : null}
        </EhrCard>
    );
};

export default Enrollment;
